// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Session } from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { EMPTY_VALUE } from "@pacific/XDL/constants";
import { BackorderModel } from "@pacific/XDL/types";
import { ellipsis, xScreen } from "@pacific/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    backorder: BackorderModel;
    session: Session;
    extendedStyles?: BackorderSummaryCardStyles;
}

type Props = OwnProps;

export interface BackorderSummaryCardStyles {
    dataCell?: DataTableCellProps;
    description?: DataTableCellProps;
}

export const invoiceSummaryCardStyles: BackorderSummaryCardStyles = {
    description: {
        css: css`
            ${xScreen("gt", "md")} {
                max-width: 400px;
                ${ellipsis(2)}
                height: 60px;
                white-space: pre-wrap;
            }
            ${xScreen("lte", "md")} {
                white-space: nowrap;
                max-width: initial;
            }
        `,
    },
};

const BackorderSummaryCard: FC<Props> = ({ backorder, session, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(invoiceSummaryCardStyles, extendedStyles));

    const data = {
        productNumber: backorder.productErpNumber,
        description: backorder.description,
        orderNumber: backorder.erpOrderNumber,
        poNumber: backorder.customerPo,
        boQty: backorder.qtyOrdered,
        date: backorder.orderDate
            ? getLocalizedDateTime({
                dateTime: new Date(backorder.orderDate),
                language: session.language,
                options: {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                },
            })
            : "",
    };

    return (
        <>
            <DataTableCell data-th={translate("Product")} {...styles.dataCell}>
                {data.productNumber || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("Description")} {...styles.description}>
                {data.description || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("Order #")} {...styles.dataCell}>
                {data.orderNumber ? <OrderDetailPageTypeLink title={data.orderNumber} orderNumber={data.orderNumber} /> : EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("PO #")} {...styles.dataCell}>
                {data.poNumber || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("BO Qty")} {...styles.dataCell}>
                {data.boQty || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("Order Date")} {...styles.dataCell}>
                {data.date || EMPTY_VALUE}
            </DataTableCell>
        </>
    );
};

export default BackorderSummaryCard;
